import camelCase from 'lodash/camelCase';
import { F24Details } from '~/types/taxes';
import { F24AmountInput, toAmountNumber } from '~/views/bookkeeping/taxes/F24PdfPreview/format';

type Raw = Record<string, unknown>;

/** Recursively camelize every object key, preserving arrays and primitive leaf values. */
const deepCamelize = (input: unknown): unknown => {
  if (Array.isArray(input)) return input.map(deepCamelize);
  if (input !== null && typeof input === 'object') {
    return Object.fromEntries(
      Object.entries(input as Raw).map(([key, value]) => [camelCase(key), deepCamelize(value)]),
    );
  }
  return input;
};

/** Section keys whose camelization does not match the `F24Details` field name. */
const SECTION_KEY_OVERRIDES: Record<string, keyof F24Details> = {
  sezione_imu: 'sezioneImuAltriTributiLocali',
  sezione_accise: 'sezioneAcciseMonopoli',
};

/**
 * A handful of detail fields are renames (or a legacy typo), not case conversions, so `deepCamelize`
 * can't produce the name the PDF renderer reads. Map the *camelized* API key -> renderer field name.
 * These match the names emitted by `buildGeneratedF24Details` (the other source feeding the renderer).
 */
const ROW_KEY_ALIASES: Record<string, string> = {
  periodoDiRiferimentoDaMmAaaa: 'periodoRiferimentoDa',
  periodoDiRiferimentoAMmAaaa: 'periodoRiferimentoA',
  annoRiferimento: 'annoPeriodoRiferimento',
  rateazioneMeseRif: 'rateazioneRegioneProvMeseRif',
};
// Altri enti / INAIL label their causale `codiceContributo`; INPS keeps `causaleContributo`.
const ALTRI_ENTI_KEY_ALIASES: Record<string, string> = { ...ROW_KEY_ALIASES, causaleContributo: 'codiceContributo' };

// Amounts arrive as strings; coerce them so both data sources feed the renderer numbers.
const AMOUNT_KEYS = new Set(['importoADebitoVersato', 'importoACreditoCompensato']);

const aliasRow = (row: unknown, aliases: Record<string, string>): unknown => {
  if (row === null || typeof row !== 'object' || Array.isArray(row)) return row;
  return Object.fromEntries(
    Object.entries(row as Raw).map(([key, value]) => {
      const name = aliases[key] ?? key;
      return [name, AMOUNT_KEYS.has(name) ? toAmountNumber(value as F24AmountInput) : value];
    }),
  );
};

/** Re-key a camelized section's `dettagli` rows; leaves the section's other fields untouched. */
const aliasDettagli = (section: unknown, aliases: Record<string, string>): unknown => {
  if (section === null || typeof section !== 'object' || Array.isArray(section)) return section;
  const { dettagli } = section as Raw;
  if (!Array.isArray(dettagli)) return section;
  return { ...(section as Raw), dettagli: dettagli.map((row) => aliasRow(row, aliases)) };
};

export const normalizeF24Details = (raw: unknown): F24Details => {
  const source = (raw ?? {}) as Raw;
  const result: Raw = {};
  for (const [key, value] of Object.entries(source)) {
    result[SECTION_KEY_OVERRIDES[key] ?? camelCase(key)] = deepCamelize(value);
  }

  result.sezioneErario = aliasDettagli(result.sezioneErario, ROW_KEY_ALIASES);
  result.sezioneInps = aliasDettagli(result.sezioneInps, ROW_KEY_ALIASES);
  result.sezioneRegioni = aliasDettagli(result.sezioneRegioni, ROW_KEY_ALIASES);
  result.sezioneImuAltriTributiLocali = aliasDettagli(result.sezioneImuAltriTributiLocali, ROW_KEY_ALIASES);
  result.sezioneAltriEnti = aliasDettagli(result.sezioneAltriEnti, ALTRI_ENTI_KEY_ALIASES);

  return result;
};
